import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { Card, Spin, Select, Table, Row, Col } from "antd";
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";

export default function OtChart() {
    const { authData, backUrl } = useContext(AuthContext);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [year, setYear] = useState(new Date().getFullYear());

    const COLORS = ["#0088FE", "#FF8042"];

    useEffect(() => {
        fetchOtData();
    }, [month, year]);

    const fetchOtData = async () => {
        try {
            setLoading(true);

            const res = await axios.get(
                `${backUrl}/api/reports/payroll-details/?month=${month}&year=${year}`,
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                    },
                }
            );
            setData(res.data);
        } catch (error) {
            console.log("Error fetching OT chart data", error);
        } finally {
            setLoading(false);
        }
    };

    const monthOptions = Array.from({ length: 12 }, (_, i) => ({
        label: `${i + 1}`.padStart(2, "0"),
        value: i + 1,
    }));

    const yearOptions = [];
    for (let y = 2020; y <= 2030; y++) {
        yearOptions.push({ label: y, value: y });
    }

    const columns = [
        { title: "Code", dataIndex: "employee_code", key: "code" },
        { title: "Name", dataIndex: "employee_name", key: "name" },
        { title: "Designation", dataIndex: "designation", key: "designation" },
        { title: "Department", dataIndex: "department", key: "department" },
        {
            title: "OT Payment",
            dataIndex: "overtime_payment",
            key: "ot",
            render: (v) => v?.toFixed(2),
        },
        {
            title: "Advance Deduction",
            dataIndex: "advance_deduction",
            key: "adv",
            render: (v) => v?.toFixed(2),
        },
    ];

    if (loading || !data)
        return (
            <Card style={{ width: "100%", height: 380 }}>
                <Spin />
            </Card>
        );

    const pieData = [
        { name: "Total Overtime Payment", value: data.total_overtime_payment },
        { name: "Total Advance Deductions", value: data.total_advance_deductions },
    ];

    return (
        <>
            {/* Filters */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={4}>
                    <label>Month</label>
                    <Select
                        style={{ width: "100%" }}
                        value={month}
                        onChange={setMonth}
                        options={monthOptions}
                    />
                </Col>

                <Col span={4}>
                    <label>Year</label>
                    <Select
                        style={{ width: "100%" }}
                        value={year}
                        onChange={setYear}
                        options={yearOptions}
                    />
                </Col>
            </Row>

            {/* Chart */}
            <Card
                title={`Payroll Summary - ${month}/${year}`}
                style={{ width: "100%", marginBottom: 20 }}
            >
                <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                        <Pie
                            data={pieData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={110}
                            label
                        >
                            {pieData.map((entry, index) => (
                                <Cell key={index} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            </Card>

            {/* Table */}
            <Card title="Employee-wise OT & Advance Details">
                <Table
                    columns={columns}
                    dataSource={data.employees_with_details}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                    components={{
                        header: {
                            cell: (props) => (
                                <th {...props} style={{ backgroundColor: "#0e497b", color: 'white', marginTop: '20px' }}>
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                />
            </Card>
        </>
    );
}
